import {
  AdminHeader,
  AdminInput,
  AdminSpinner,
  Box,
  Button,
  ChevronSmall,
  Disclaimer,
  Div,
  DownloadIcon,
  ErrorToast,
  H3,
  H5,
  Icon,
  ImagePlaceholderIcon,
  ImagePreview,
  IntegrationAccessModal,
  Logo,
  P,
  PayoutConfigEditor,
  PayoutConfigResponse,
  PhoneNumberText,
  posDisplayName,
  posImage,
  ReferralIcon,
  SuccessToast,
  downloadImageFromURL,
  ensurePayoutConfigs,
  getLocationById,
  lightTheme,
  queryKeys,
  marketplaceLogo,
  reportError,
  taxRateDraftToDto,
  toPayoutConfigsPayload,
  validatePayoutConfigs,
  validateVerificationTaxRates,
  verifyLocation,
} from '@vgrubs/components';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import { VerifyLocationRequestData } from 'components/src/types/api/location.types';
import { LocationEntity } from 'components/src/types/location.types';
import { TaxRateDraft } from 'components/src/types/taxRate.types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OpenConversationButton } from '../Chat/OpenConversationButton';
import { Services } from '../Locations/services/Services';
import { RejectModal } from './RejectModal';
import { IntegrationTile } from './components/IntegrationTile';
import { KeyValueInfo } from './components/KeyValueInfo';
import { SectionCard } from './components/SectionCard';
import { VerificationTaxRates } from './components/VerificationTaxRates';
import { ACH_CUSTOMER_ID_TOOLTIP } from '../VDrive/vDriveHelpers';

/**
 * One restaurant waiting for verification. Left: what the restaurant submitted, read-only.
 * Right: what the admin sets before verifying. Verify and Reject sit in the header.
 */
export const RestaurantDetails = () => {
  const [loading, setLoading] = useState(false);
  const [integrationModal, setIntegrationModal] = useState(false);
  const [location, setLocation] = useState<LocationEntity | null>(null);
  const [payoutConfigs, setPayoutConfigs] = useState<PayoutConfigResponse[]>(
    []
  );
  // The location's stored payout configs (defaults until it is verified), for
  // the editor to restore exceptions from on a switch back to the saved model.
  const savedPayoutConfigs = useMemo(
    () => ensurePayoutConfigs(location?.payoutConfigs),
    [location?.payoutConfigs]
  );
  const [achCustomerId, setAchCustomerId] = useState('');
  // Entered here and written by the verify call itself - the location has no
  // taxes of its own until it is verified.
  const [taxRates, setTaxRates] = useState<TaxRateDraft[]>([]);
  const [payoutCollapse, setPayoutCollapse] = useState(false);
  const navigate = useNavigate();

  const [rejectModal, setRejectModal] = useState<string | number | null>(null);

  const { id } = useParams() as { id: string };
  const { invalidateByKey } = useInvalidateQueries();

  async function handleVerifyLocation() {
    const payoutError = validatePayoutConfigs(payoutConfigs);
    if (payoutError) return ErrorToast(payoutError);

    // The same two rules the backend applies, said before the request goes out
    // rather than after the whole form has been filled in.
    const taxError = validateVerificationTaxRates(taxRates);
    if (taxError) return ErrorToast(taxError);

    const body: VerifyLocationRequestData = {
      payoutConfigs: toPayoutConfigsPayload(payoutConfigs),
      achCustomerId,
      mode: location?.mode,
      taxRates: taxRates.map(taxRateDraftToDto),
    };

    try {
      setLoading(true);
      const { data } = await verifyLocation(id, body);
      SuccessToast(data.message);
      invalidateByKey(queryKeys.admin.verifications.count);
      invalidateByKey(queryKeys.admin.verifications.restaurants);
      navigate(-1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGetLocation() {
    const dateNow = new Date();
    try {
      setLoading(true);
      const { data } = await getLocationById(id, {
        from: dateNow,
        to: dateNow,
      });

      setLocation(data.data);
      setPayoutConfigs(ensurePayoutConfigs(data.data.payoutConfigs));
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetLocation();
    }
  }, []);

  const {
    name,
    address,
    owner,
    phone,
    businessLicense,
    menuSignInProviders,
    city,
    state,
    zipCode,
    country,
    referral,
  } = location || {};

  const fullAddress = `${address} ${city} ${state} ${zipCode} ${country}`;

  // What the restaurant said it runs on, from the location's own integrationAccess (the
  // same GET, passwords stripped). A response without it, from an API that predates the
  // field, still lists the marketplaces, just with no access state.
  const access = location?.integrationAccess ?? null;
  const pos = access?.pos ?? null;
  const marketplaces = access
    ? access.providers.map((p) => ({
        key: p.menuProviderId,
        name: p.name,
        type: p.type,
        granted: p.accessGranted as boolean | undefined,
      }))
    : (menuSignInProviders ?? []).map((p, i) => ({
        key: p.id ?? i,
        name: p.name,
        type: p.type,
        granted: undefined as boolean | undefined,
      }));
  const accessCaption = (granted?: boolean) =>
    granted === undefined
      ? undefined
      : granted
      ? 'Access granted'
      : 'Access pending';

  return (
    <>
      <AdminHeader>
        <OpenConversationButton userId={owner?.id} role={owner?.role} />
      </AdminHeader>
      {loading && <AdminSpinner />}
      {location && (
        <>
          <Div
            $flex
            $columnSM
            $gap="32px"
            $gapSM="20px"
            $m="32px 0"
            $mSM="20px 0"
            $between
          >
            <Div $flex $alignCenter $gap="20px" $gapSM="16px">
              <Logo width="100" height="100" />
              <Div>
                <H3 $mb="8" $color={lightTheme.primary}>
                  {name}
                </H3>
                <P $m="0">{fullAddress}</P>
              </Div>
            </Div>

            <Div
              $flex
              $alignEnd
              $justifyCenter
              $column
              $gap="16px"
              $widthSM="100%"
            >
              <Button
                $selected
                $gray
                $w="250px"
                $wSM="100%"
                onClick={() => setRejectModal(id)}
              >
                Reject Restaurant
              </Button>
              <Button
                $selected
                $w="250px"
                $wSM="100%"
                onClick={handleVerifyLocation}
              >
                Verify Restaurant
              </Button>
            </Div>
          </Div>

          <Div $flex $columnSM $gap="32px" $gapSM="24px">
            <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Submitted by the restaurant
              </P>

              <SectionCard title="Owner">
                <KeyValueInfo
                  k="Name"
                  v={owner?.firstName + ' ' + owner?.lastName}
                />
                <a href={`mailto:${owner?.email}`}>
                  <KeyValueInfo k="Email" v={owner?.email} />
                </a>
                <KeyValueInfo
                  k="Phone"
                  v={phone}
                  component={<PhoneNumberText phone={phone ?? ''} title />}
                  last
                />
              </SectionCard>

              <SectionCard
                title="POS and marketplaces"
                action={
                  <Button
                    $gray
                    $small
                    $w="180px"
                    $wSM="180px"
                    onClick={() => setIntegrationModal(true)}
                  >
                    Integration Access
                  </Button>
                }
              >
                {!pos && marketplaces.length === 0 ? (
                  <P $m="0" $color={lightTheme.darkGray}>
                    Nothing selected during onboarding.
                  </P>
                ) : (
                  <Div $flex $wrap $gap="12px">
                    {pos && (
                      <IntegrationTile
                        image={posImage(pos.type)}
                        label={posDisplayName(pos.type)}
                        caption={`POS · ${accessCaption(pos.accessGranted)}`}
                      />
                    )}
                    {marketplaces.map((m) => (
                      <IntegrationTile
                        key={m.key}
                        image={marketplaceLogo(m.type, m.name)}
                        label={m.name}
                        caption={accessCaption(m.granted)}
                      />
                    ))}
                  </Div>
                )}
              </SectionCard>

              <SectionCard
                title="Business License"
                action={
                  businessLicense ? (
                    <Icon
                      $pointer
                      onClick={() =>
                        downloadImageFromURL(
                          businessLicense,
                          `Agent ${name} Business License`
                        )
                      }
                    >
                      <DownloadIcon />
                    </Icon>
                  ) : undefined
                }
              >
                {businessLicense ? (
                  <Div $flex $justifyCenter>
                    <ImagePreview image={businessLicense} lightbox />
                  </Div>
                ) : (
                  <Div $m="24px 0" $flex $column $alignCenter>
                    <ImagePlaceholderIcon />
                    <Disclaimer $center $m="0">
                      Not uploaded yet
                    </Disclaimer>
                  </Div>
                )}
              </SectionCard>
            </Div>

            <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Set up before verifying
              </P>

              <Box $p="20px 24px" $pSM="16px">
                <Div
                  $pointer
                  $flex
                  $alignCenter
                  $between
                  onClick={() => setPayoutCollapse((prev) => !prev)}
                >
                  <H5 $mb="0">Payout Configuration</H5>
                  <Icon $rotate={payoutCollapse ? '180deg' : '0'}>
                    <ChevronSmall />
                  </Icon>
                </Div>
                {payoutCollapse && (
                  <Div $flex $column $gap="8px" $p="16px 0 0 0">
                    <PayoutConfigEditor
                      configs={payoutConfigs}
                      savedConfigs={savedPayoutConfigs}
                      onChange={setPayoutConfigs}
                    />
                  </Div>
                )}
              </Box>

              <Box $p="20px 24px" $pSM="16px">
                <AdminInput
                  tooltip={ACH_CUSTOMER_ID_TOOLTIP}
                  label="ACH Customer ID"
                  placeholder="Enter here"
                  value={achCustomerId}
                  onChange={({ target }) => setAchCustomerId(target.value)}
                  $textCenter
                />
              </Box>

              <VerificationTaxRates
                locationId={id}
                taxRates={taxRates}
                setTaxRates={setTaxRates}
              />

              <Services
                setLoading={setLoading}
                id={id}
                salesConfig={location.salesConfig}
                setData={setLocation}
                componentText="Services the client is interested in"
              />

              <SectionCard title="Onboarding Sales">
                {referral ? (
                  <Div
                    $flex
                    $between
                    $alignCenter
                    $pointer
                    onClick={() => navigate(`/users/agent/${referral.id}`)}
                  >
                    <Div>
                      <H5 $mb="0">
                        {referral.firstName} {referral.lastName}
                      </H5>
                      <P $mb="0">{referral.phone.number}</P>
                      <P $mb="0" $color={lightTheme.lightGray}>
                        Referral
                      </P>
                    </Div>
                    <Icon>
                      <ReferralIcon />
                    </Icon>
                  </Div>
                ) : (
                  <Div $flex $between $alignCenter>
                    <P $m="0" $color={lightTheme.darkGray}>
                      No referral
                    </P>
                    <Icon>
                      <ReferralIcon />
                    </Icon>
                  </Div>
                )}
              </SectionCard>
            </Div>
          </Div>

          <IntegrationAccessModal
            modalIsOpen={integrationModal}
            closeModal={() => setIntegrationModal(false)}
            locationId={id}
          />
        </>
      )}
      <RejectModal
        modalIsOpen={rejectModal ?? ''}
        closeModal={() => setRejectModal(null)}
        setLoading={setLoading}
      />
    </>
  );
};
